/**
 * Zod-driven section property forms (Forge builder v3, ported from
 * growwithhype).
 *
 * The old VV editor hand-wrote a page manifest declaring every editable
 * field; builder-v3 pages are typed sections, so the editor GENERATES the
 * property panel by introspecting the section's Zod object
 * (`SECTION_SCHEMAS[type].shape`). Adding a section type to @vv/sections
 * gives it an editor for free.
 *
 * Widget dispatch (per field, after unwrapping Optional/Default/Nullable):
 *   ZodString  → text input | textarea (name/length heuristic) | ImagePicker
 *                (name heuristic: src / image / *Image / avatarUrl / ogImage)
 *   ZodNumber  → number input
 *   ZodBoolean → checkbox
 *   ZodEnum    → select
 *   ZodArray<string> → ListEditor (one input per item — stack convention)
 *   ZodArray<object> → ItemList (collapsible item cards, add/remove/move)
 *   ZodObject  → nested fieldset (optional objects get add/remove toggle)
 *
 * Values flow one way: every widget calls onChange with the WHOLE new
 * section object; PagesEditor hands it to usePageDocs.setSectionData which
 * stages the draft + schedules autosave.
 *
 * Click-to-edit: every field wrapper carries `data-fg-path` — the
 * section-relative dot path ("title", "rows.2.value", "images.0").
 * SchemaForm's focusPath prop scrolls the matching wrapper into view,
 * auto-expands the ItemList item containing it, flash-highlights it, and
 * focuses its first input (nonce lets the same path re-fire).
 *
 * Record lists (Lists mode) reuse the same machinery through SchemaForm
 * with per-list `fieldMeta` (labels, hints, widget overrides such as a
 * group select or an image aspect) and `plainText` — record copy renders
 * as text on the site, so the "Supports HTML" hints don't apply.
 * `*_cents` numbers edit as dollars.
 */

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { z } from 'zod';
import { ImagePicker } from '~/components/ImagePicker';
import ListEditor from '~/components/ListEditor';
import { SECTION_SCHEMAS, SECTION_INTERNAL_FIELDS, type SectionType } from '@vv/sections';

/** Per-field overrides for a generated form (root-level fields only). */
export interface FieldMeta {
  label?: string;
  /** Help line under the field; null suppresses the built-in hint. */
  hint?: string | null;
  widget?: 'text' | 'textarea' | 'image' | 'money' | 'select';
  /** Choices for widget 'select' (strings) or labels for an enum. */
  options?: Array<{ value: string; label: string }>;
  /** ImagePicker preview aspect ("16/10") — match the site render. */
  aspect?: string;
  placeholder?: string;
}

/** Form-wide switches, read by every Field without prop drilling. */
const FormOptions = createContext<{ plainText: boolean }>({ plainText: false });

// ── Heuristics ─────────────────────────────────────────────────────

const IMAGE_FIELD_RE = /^(src|image|image_url|avatarUrl|ogImage)$|Image$/;
const MONEY_FIELD_RE = /_cents$/;
const TEXTAREA_FIELD_RE =
  /^(body|blurb|html|description|answer|quote|tldr|caption|text|note|imageCaption|consultBody|directBody|formNote)$/;

/** Aspect-ratio hints for the ImagePicker preview — match how the field
 * renders on the public site so uploads aren't cropped by surprise. */
const ASPECT_HINTS: Record<string, string> = {
  src: '4/3', // gallery tiles
  image: '4/5', // hero portraits / media-text
  backgroundImage: '16/9',
  coverImage: '4/3',
  avatarUrl: '1/1',
  ogImage: '1200/630',
};

/** Nicer labels where camelCase prettifying isn't enough. */
const LABEL_OVERRIDES: Record<string, string> = {
  n: 'Number label',
  ctas: 'Buttons',
  cta: 'Button',
  href: 'Link',
  eyebrow: 'Eyebrow (kicker)',
  imageAlt: 'Image alt text',
  alt: 'Alt text',
  ogImage: 'Social share image (OG)',
  noindex: 'Hide from search engines',
  jsonLd: 'JSON-LD',
  pageKey: 'FAQ group (faqs.page key)',
  fromSteps: 'Pull steps from (program_steps page key)',
  leftText: 'Left text',
  hoursLabel: 'Hours — label',
  hoursValue: 'Hours — value',
  statValue: 'Stat — number',
  statLabel: 'Stat — label',
  ribbonLeft: 'Ribbon — left text',
  ribbonRight: 'Ribbon — right text',
  signatureName: 'Signature — name',
  signatureSub: 'Signature — credentials',
  includedItems: 'Included items',
  includedEyebrow: 'Included — eyebrow',
  includedTitle: 'Included — headline',
  consultTitle: 'Consult door — title',
  consultBody: 'Consult door — body',
  directTitle: 'Direct-signup door — title',
  directBody: 'Direct-signup door — body',
  directCtaLabel: 'Direct-signup door — button label',
  drawerCall: 'Show call button in mobile menu',
  contactNote: 'Contact column note (hours line)',
};

export function labelFor(name: string): string {
  const override = LABEL_OVERRIDES[name];
  if (override) return override;
  const spaced = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Enum option text: "numbered-dark" → "Numbered dark". */
function optionLabel(value: string, meta?: FieldMeta): string {
  const hit = meta?.options?.find((o) => o.value === value);
  if (hit) return hit.label;
  const spaced = value.replace(/[-_]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** "$" input over an integer-cents number. Keeps its own text while typing
 * ("12." is mid-entry, not 12) and resyncs when the value changes from
 * outside (undo, another tab's reload). Unparseable text commits
 * undefined — the validation gate then holds the save. */
function MoneyInput({
  cents,
  onChange,
  style,
}: {
  cents: number | undefined;
  onChange: (next: number | undefined) => void;
  style: React.CSSProperties;
}) {
  const toText = (c: number | undefined) =>
    typeof c === 'number' ? (c % 100 === 0 ? String(c / 100) : (c / 100).toFixed(2)) : '';
  const [text, setText] = useState(() => toText(cents));
  const committed = useRef(cents);
  useEffect(() => {
    if (cents !== committed.current) {
      committed.current = cents;
      setText(toText(cents));
    }
  }, [cents]);
  return (
    <div style={{ position: 'relative' }}>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-muted)',
          fontSize: 14,
          pointerEvents: 'none',
        }}
      >
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        style={{ ...style, paddingLeft: 24 }}
        value={text}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          const clean = raw.replace(/[$,\s]/g, '');
          const next = /^\d+(\.\d{0,2})?$/.test(clean)
            ? Math.round(parseFloat(clean) * 100)
            : undefined;
          committed.current = next;
          onChange(next);
        }}
      />
    </div>
  );
}

/** Headline fields get a formatting hint under the input. */
const HEADLINE_HINT_FIELDS = new Set(['title']);

// ── Zod unwrapping ─────────────────────────────────────────────────

interface Unwrapped {
  schema: z.ZodTypeAny;
  optional: boolean;
  hasDefault: boolean;
  defaultValue?: unknown;
}

function unwrap(schema: z.ZodTypeAny): Unwrapped {
  let s = schema;
  let optional = false;
  let hasDefault = false;
  let defaultValue: unknown;
  // Peel Optional/Default/Nullable/Effects wrappers (they can nest).
  // ZodEffects = .refine()/.transform(): ImageSrc is a refined string, and
  // without this its field matched no widget and silently never rendered —
  // no photo upload anywhere in the section forms.
  for (;;) {
    const def = s._def as {
      typeName?: string;
      innerType?: z.ZodTypeAny;
      schema?: z.ZodTypeAny;
      defaultValue?: () => unknown;
    };
    if (def.typeName === 'ZodOptional' || def.typeName === 'ZodNullable') {
      optional = true;
      s = def.innerType!;
    } else if (def.typeName === 'ZodDefault') {
      hasDefault = true;
      defaultValue = def.defaultValue!();
      s = def.innerType!;
    } else if (def.typeName === 'ZodEffects' && def.schema) {
      s = def.schema;
    } else {
      break;
    }
  }
  return { schema: s, optional, hasDefault, defaultValue };
}

function typeName(schema: z.ZodTypeAny): string {
  return (schema._def as { typeName: string }).typeName;
}

/** Build a valid-ish default value for a schema (new array items, optional
 * objects). The validation gate catches anything this gets wrong. */
export function buildDefault(schema: z.ZodTypeAny, fieldName = ''): unknown {
  const u = unwrap(schema);
  if (u.hasDefault) return u.defaultValue;
  const t = typeName(u.schema);
  if (t === 'ZodString') {
    const named: Record<string, string> = {
      title: 'New item',
      label: 'Label',
      value: 'Value',
      question: 'New question?',
      answer: 'The answer.',
      description: 'Describe it.',
      quote: 'Quote text.',
      body: 'Body copy.',
      days: 'Mon – Fri',
      href: '/contact',
      slug: 'new-item',
      name: 'New item',
      price: '$0',
      alt: 'Describe the photo',
      n: '01',
    };
    return named[fieldName] ?? 'Text';
  }
  if (t === 'ZodNumber') return 1;
  if (t === 'ZodBoolean') return false;
  if (t === 'ZodEnum') return (u.schema._def as { values: string[] }).values[0];
  if (t === 'ZodLiteral') return (u.schema._def as { value: unknown }).value;
  if (t === 'ZodArray') {
    const def = u.schema._def as { type: z.ZodTypeAny; minLength: { value: number } | null };
    const min = def.minLength?.value ?? 0;
    return Array.from({ length: min }, () => buildDefault(def.type));
  }
  if (t === 'ZodObject') {
    const shape = (u.schema as z.ZodObject<z.ZodRawShape>).shape;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(shape)) {
      const uv = unwrap(v as z.ZodTypeAny);
      if (uv.optional && !uv.hasDefault) continue; // leave optionals absent
      out[k] = buildDefault(v as z.ZodTypeAny, k);
    }
    return out;
  }
  return undefined;
}

/** Pick a human label for an object-array item (card, step, row, …). */
export function itemLabel(item: Record<string, unknown>, index: number): string {
  for (const k of ['title', 'label', 'question', 'name', 'author', 'days', 'quote', 'alt', 'number']) {
    const v = item[k];
    if (typeof v === 'string' && v.trim()) {
      const text = v.replace(/<[^>]+>/g, ''); // strip markup for display
      return text.length > 42 ? `${text.slice(0, 42)}…` : text;
    }
  }
  return `Item ${index + 1}`;
}

// ── Shared field chrome ────────────────────────────────────────────

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: 'var(--primary)',
  textTransform: 'uppercase',
  display: 'block',
  marginBottom: 4,
};

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  fontFamily: 'inherit',
  fontSize: 14,
  padding: '10px 12px',
  border: '1px solid var(--line)',
  borderRadius: 8,
  background: 'var(--card)',
  color: 'var(--text)',
  boxSizing: 'border-box',
};

const HINT_STYLE: React.CSSProperties = {
  fontSize: 11.5,
  color: 'var(--text-muted)',
  marginTop: 4,
};

function FieldWrap({
  label,
  path,
  children,
  hint,
}: {
  label: string;
  path: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div data-fg-path={path}>
      <label style={LABEL_STYLE}>{label}</label>
      {children}
      {hint && <div style={HINT_STYLE}>{hint}</div>}
    </div>
  );
}

// ── Field dispatch ─────────────────────────────────────────────────

/** Preview click-to-edit target. The nonce re-fires the same path. */
interface FocusPath {
  path: string;
  nonce: number;
}

interface FieldProps {
  name: string;
  /** Section-relative dot path of this field ("title", "rows.2.value"). */
  path: string;
  schema: z.ZodTypeAny;
  value: unknown;
  onChange: (next: unknown) => void;
  openPath: FocusPath | null;
  meta?: FieldMeta;
}

function Field({ name, path, schema, value, onChange, openPath, meta }: FieldProps) {
  const u = unwrap(schema);
  const t = typeName(u.schema);
  const label = meta?.label ?? labelFor(name);
  const { plainText } = useContext(FormOptions);
  /** meta.hint wins (null = none); built-in HTML hints only on rich forms. */
  const hintOr = (builtIn?: string) =>
    meta?.hint === null ? undefined : (meta?.hint ?? (plainText ? undefined : builtIn));

  if (t === 'ZodString') {
    const str = typeof value === 'string' ? value : '';
    // Optional string fields clear back to ABSENT (not ''), so optional
    // sub-objects/images don't linger as empty strings in the JSON.
    const commit = (next: string) =>
      onChange(next === '' && u.optional ? undefined : next);

    if (meta?.widget === 'select' && meta.options) {
      const known = meta.options.some((o) => o.value === str);
      return (
        <FieldWrap label={label} path={path} hint={hintOr()}>
          <select style={INPUT_STYLE} value={str} onChange={(e) => commit(e.target.value)}>
            {!known && <option value={str}>{str || '—'}</option>}
            {meta.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FieldWrap>
      );
    }
    if (meta?.widget === 'image' || (meta?.widget === undefined && IMAGE_FIELD_RE.test(name))) {
      return (
        <FieldWrap label={label} path={path} hint={hintOr()}>
          <ImagePicker
            value={str}
            onChange={commit}
            aspect={meta?.aspect ?? ASPECT_HINTS[name] ?? '16/10'}
          />
        </FieldWrap>
      );
    }
    const isTextarea =
      meta?.widget === 'textarea' ||
      (meta?.widget !== 'text' && (TEXTAREA_FIELD_RE.test(name) || str.length > 80));
    if (isTextarea) {
      const rows = name === 'html' ? 8 : Math.min(10, Math.max(3, Math.ceil(str.length / 60)));
      return (
        <FieldWrap
          label={label}
          path={path}
          hint={hintOr(
            name === 'html' || name === 'body'
              ? 'Supports HTML — <p>, <br />, <em>, <span> for styled words.'
              : undefined,
          )}
        >
          <textarea
            style={{
              ...INPUT_STYLE,
              resize: 'vertical',
              ...(name === 'html'
                ? { fontFamily: 'var(--font-mono, monospace)', fontSize: 12.5 }
                : null),
            }}
            rows={rows}
            value={str}
            onChange={(e) => commit(e.target.value)}
          />
        </FieldWrap>
      );
    }
    return (
      <FieldWrap
        label={label}
        path={path}
        hint={hintOr(
          HEADLINE_HINT_FIELDS.has(name)
            ? 'Supports HTML — <br /> line breaks, styled <span>s, <em> italics.'
            : undefined,
        )}
      >
        <input
          type="text"
          style={INPUT_STYLE}
          value={str}
          placeholder={meta?.placeholder}
          onChange={(e) => commit(e.target.value)}
        />
      </FieldWrap>
    );
  }

  if (t === 'ZodNumber') {
    if (meta?.widget === 'money' || MONEY_FIELD_RE.test(name)) {
      return (
        <FieldWrap label={label} path={path} hint={hintOr()}>
          <MoneyInput
            cents={typeof value === 'number' ? value : undefined}
            onChange={(next) => onChange(next)}
            style={INPUT_STYLE}
          />
        </FieldWrap>
      );
    }
    const num = typeof value === 'number' ? value : '';
    return (
      <FieldWrap label={label} path={path} hint={hintOr()}>
        <input
          type="number"
          style={INPUT_STYLE}
          value={num}
          placeholder={meta?.placeholder}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange(u.optional ? undefined : u.hasDefault ? u.defaultValue : undefined);
            } else {
              onChange(Number(raw));
            }
          }}
        />
      </FieldWrap>
    );
  }

  if (t === 'ZodBoolean') {
    const checked =
      typeof value === 'boolean' ? value : u.hasDefault ? Boolean(u.defaultValue) : false;
    const hint = hintOr();
    return (
      <div data-fg-path={path}>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text)' }}
        >
          <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
          {label}
        </label>
        {hint && <div style={{ ...HINT_STYLE, marginLeft: 24 }}>{hint}</div>}
      </div>
    );
  }

  if (t === 'ZodEnum') {
    const values = (u.schema._def as { values: string[] }).values;
    const cur =
      typeof value === 'string' ? value : u.hasDefault ? String(u.defaultValue) : values[0];
    return (
      <FieldWrap label={label} path={path} hint={hintOr()}>
        <select style={INPUT_STYLE} value={cur} onChange={(e) => onChange(e.target.value)}>
          {values.map((v) => (
            <option key={v} value={v}>
              {optionLabel(v, meta)}
            </option>
          ))}
        </select>
      </FieldWrap>
    );
  }

  if (t === 'ZodArray') {
    const def = u.schema._def as { type: z.ZodTypeAny };
    const elT = typeName(unwrap(def.type).schema);
    const arr = Array.isArray(value) ? value : [];

    if (elT === 'ZodString') {
      return (
        <FieldWrap label={label} path={path} hint={hintOr()}>
          <ListEditor
            items={arr.map((v) => String(v ?? ''))}
            onChange={(next) => onChange(next)}
          />
        </FieldWrap>
      );
    }
    if (elT === 'ZodObject') {
      return (
        <FieldWrap label={label} path={path}>
          <ItemList
            elementSchema={unwrap(def.type).schema as z.ZodObject<z.ZodRawShape>}
            items={arr as Record<string, unknown>[]}
            onChange={(next) => onChange(next)}
            path={path}
            openPath={openPath}
          />
        </FieldWrap>
      );
    }
    return null; // unknown[] (jsonLd) — edited in the page-meta panel, not here
  }

  if (t === 'ZodObject') {
    const objSchema = u.schema as z.ZodObject<z.ZodRawShape>;
    const obj = value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
    if (!obj) {
      // Optional nested object (process.cta, faq.cta) — add on demand.
      return (
        <FieldWrap label={label} path={path}>
          <button
            type="button"
            className="fg-add-btn"
            onClick={() => onChange(buildDefault(objSchema, name))}
          >
            + Add {labelFor(name).toLowerCase()}
          </button>
        </FieldWrap>
      );
    }
    return (
      <FieldWrap label={label} path={path}>
        <div className="fg-nested">
          <ObjectFields
            shape={objSchema.shape}
            value={obj}
            onChange={(next) => onChange(next)}
            path={path}
            openPath={openPath}
          />
          {u.optional && (
            <button type="button" className="fg-remove-btn" onClick={() => onChange(undefined)}>
              × Remove {labelFor(name).toLowerCase()}
            </button>
          )}
        </div>
      </FieldWrap>
    );
  }

  return null; // ZodUnknown etc. — not editable here
}

// ── Object walker ──────────────────────────────────────────────────

function ObjectFields({
  shape,
  value,
  onChange,
  path,
  openPath,
  skip = [],
  fieldMeta,
}: {
  shape: z.ZodRawShape;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Dot-path prefix of this object ('' at the section root). */
  path: string;
  openPath: FocusPath | null;
  skip?: string[];
  /** Root-level overrides only — nested objects/items use the defaults. */
  fieldMeta?: Record<string, FieldMeta>;
}) {
  const set = (key: string, next: unknown) => {
    const out = { ...value };
    if (next === undefined) delete out[key];
    else out[key] = next;
    onChange(out);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {Object.entries(shape).map(([name, fieldSchema]) => {
        // SECTION_INTERNAL_FIELDS = machine-managed keys ('type', 'hidden' —
        // the eye toggle owns hidden); never render them as form fields.
        if (SECTION_INTERNAL_FIELDS.has(name) || skip.includes(name)) return null;
        return (
          <Field
            key={name}
            name={name}
            path={path ? `${path}.${name}` : name}
            schema={fieldSchema as z.ZodTypeAny}
            value={value[name]}
            onChange={(next) => set(name, next)}
            openPath={openPath}
            meta={fieldMeta?.[name]}
          />
        );
      })}
    </div>
  );
}

// ── Object-array item list (cards / steps / rows / …) ──────────────

function ItemList({
  elementSchema,
  items,
  onChange,
  path,
  openPath,
}: {
  elementSchema: z.ZodObject<z.ZodRawShape>;
  items: Record<string, unknown>[];
  onChange: (next: Record<string, unknown>[]) => void;
  /** Dot path of the array itself ("rows", "steps"). */
  path: string;
  openPath: FocusPath | null;
}) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  // focusPath auto-expand: a path INTO this list ("rows.2.value") opens
  // item 2 so the target field mounts. Nonce-guarded (no deps array — the
  // guard is the gate) so ordinary re-renders don't fight the user
  // re-collapsing the item, and so a list mounted mid-focus (nested lists)
  // still applies the current target.
  const appliedNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!openPath || appliedNonce.current === openPath.nonce) return;
    appliedNonce.current = openPath.nonce;
    if (!openPath.path.startsWith(`${path}.`)) return;
    const idx = Number(openPath.path.slice(path.length + 1).split('.')[0]);
    if (Number.isInteger(idx) && idx >= 0 && idx < items.length) setOpenIdx(idx);
  });

  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it!);
    onChange(next);
    // Follow whichever item was EXPANDED to its new index — moving a
    // sibling must not steal the open state.
    setOpenIdx((cur) => {
      if (cur === null) return null;
      if (cur === from) return to;
      if (from < cur && to >= cur) return cur - 1;
      if (from > cur && to <= cur) return cur + 1;
      return cur;
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((item, i) => {
        const open = openIdx === i;
        return (
          <div key={i} className={`fg-item${open ? ' is-open' : ''}`} data-fg-path={`${path}.${i}`}>
            <div className="fg-item__head">
              <button
                type="button"
                className="fg-item__toggle"
                aria-expanded={open}
                onClick={() => setOpenIdx(open ? null : i)}
              >
                <span className="fg-item__chevron" aria-hidden>
                  ▸
                </span>
                {itemLabel(item, i)}
              </button>
              <span className="fg-item__tools">
                <button type="button" title="Move up" aria-label={`Move item ${i + 1} up`} onClick={() => move(i, i - 1)} disabled={i === 0}>
                  ↑
                </button>
                <button type="button" title="Move down" aria-label={`Move item ${i + 1} down`} onClick={() => move(i, i + 1)} disabled={i === items.length - 1}>
                  ↓
                </button>
                <button
                  type="button"
                  title="Remove"
                  aria-label={`Remove item ${i + 1}`}
                  onClick={() => {
                    if (!window.confirm(`Remove "${itemLabel(item, i)}"?`)) return;
                    onChange(items.filter((_, j) => j !== i));
                    setOpenIdx((cur) =>
                      cur === null || cur === i ? null : cur > i ? cur - 1 : cur,
                    );
                  }}
                >
                  ×
                </button>
              </span>
            </div>
            {open && (
              <div className="fg-item__body">
                <ObjectFields
                  shape={elementSchema.shape}
                  value={item}
                  onChange={(next) => onChange(items.map((it, j) => (j === i ? next : it)))}
                  path={`${path}.${i}`}
                  openPath={openPath}
                />
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="fg-add-btn"
        onClick={() => {
          onChange([...items, buildDefault(elementSchema) as Record<string, unknown>]);
          setOpenIdx(items.length);
        }}
      >
        + Add item
      </button>
    </div>
  );
}

// ── Public API ─────────────────────────────────────────────────────

/** Longest mounted data-fg-path that is a dot-prefix of `path` — the focus
 * fallback when the exact node doesn't exist (absent optional object, the
 * site's parent-path convention for gallery imgs, an index past the list). */
function longestPrefixNode(root: HTMLElement, path: string): HTMLElement | null {
  let best: HTMLElement | null = null;
  let bestLen = -1;
  for (const el of Array.from(root.querySelectorAll<HTMLElement>('[data-fg-path]'))) {
    const p = el.dataset.fgPath;
    if (!p || (p !== path && !path.startsWith(`${p}.`))) continue;
    if (p.length > bestLen) {
      best = el;
      bestLen = p.length;
    }
  }
  return best;
}

const FOCUSABLE_SELECTOR = 'input:not([type="file"]):not([type="hidden"]), textarea, select';

/**
 * Generic Zod-object form — the same generated-widget machinery as
 * SectionForm, for non-section schemas (NavConfig / FooterConfig). No
 * focusPath plumbing; the caller owns validation + persistence.
 */
export function ZodObjectForm({
  schema,
  value,
  onChange,
}: {
  schema: z.ZodObject<z.ZodRawShape>;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  return (
    <ObjectFields shape={schema.shape} value={value} onChange={onChange} path="" openPath={null} />
  );
}

export function SectionForm({
  type,
  value,
  onChange,
  focusPath,
}: {
  type: SectionType;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  focusPath?: { path: string; nonce: number } | null;
}) {
  const schema = SECTION_SCHEMAS[type];
  if (!schema) return <p style={HINT_STYLE}>Unknown section type "{type}".</p>;
  return <SchemaForm schema={schema} value={value} onChange={onChange} focusPath={focusPath} />;
}

/**
 * The generated form with click-to-edit focus — SectionForm's engine, open
 * to any Zod object (record lists pass their RECORD_SCHEMAS entry plus
 * per-field meta). Keyed remounts are the caller's job (switching records
 * of the same list must not leak ItemList open-state).
 */
export function SchemaForm({
  schema,
  value,
  onChange,
  focusPath,
  fieldMeta,
  skip,
  plainText = false,
}: {
  schema: z.ZodObject<z.ZodRawShape>;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  focusPath?: { path: string; nonce: number } | null;
  fieldMeta?: Record<string, FieldMeta>;
  /** Root keys never rendered (machine-managed, owned elsewhere in the UI). */
  skip?: string[];
  /** Copy renders as text on the site — drop the "Supports HTML" hints. */
  plainText?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const flashRef = useRef<{ el: HTMLElement; timer: number } | null>(null);

  // Field-focus contract: find the wrapper for focusPath.path, scroll it
  // into view, flash it, focus its first input. ItemList expansion needs a
  // commit before the target mounts (nested lists need several) — hence the
  // rAF retry loop: retry while the exact node is missing or its ItemList
  // item is still collapsed, then settle on the longest mounted prefix.
  useEffect(() => {
    if (!focusPath) return;
    const { path } = focusPath;
    let cancelled = false;
    let tries = 0;
    const seek = () => {
      if (cancelled || !rootRef.current) return;
      const root = rootRef.current;
      const exact = root.querySelector<HTMLElement>(
        `[data-fg-path="${path.replace(/["\\]/g, '\\$&')}"]`,
      );
      const stillCollapsed =
        exact?.classList.contains('fg-item') && !exact.classList.contains('is-open');
      if ((!exact || stillCollapsed) && tries < 8) {
        tries += 1;
        requestAnimationFrame(seek);
        return;
      }
      const target = exact ?? longestPrefixNode(root, path);
      if (!target) return;
      target.scrollIntoView({ block: 'center' });
      if (flashRef.current) {
        window.clearTimeout(flashRef.current.timer);
        flashRef.current.el.classList.remove('fg-flash');
      }
      target.classList.remove('fg-flash');
      void target.offsetWidth; // reflow so a same-path re-fire restarts the animation
      target.classList.add('fg-flash');
      flashRef.current = {
        el: target,
        timer: window.setTimeout(() => {
          target.classList.remove('fg-flash');
          flashRef.current = null;
        }, 1200),
      };
      target.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus({ preventScroll: true });
    };
    requestAnimationFrame(seek);
    return () => {
      cancelled = true;
    };
  }, [focusPath?.path, focusPath?.nonce]);

  return (
    <FormOptions.Provider value={{ plainText }}>
      <div ref={rootRef}>
        <ObjectFields
          shape={schema.shape}
          value={value}
          onChange={onChange}
          path=""
          openPath={focusPath ?? null}
          skip={skip}
          fieldMeta={fieldMeta}
        />
      </div>
    </FormOptions.Provider>
  );
}
